import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useStripe, useElements, ExpressCheckoutElement } from '@stripe/react-stripe-js'
import {
  StripeExpressCheckoutElementReadyEvent,
  StripeExpressCheckoutElementConfirmEvent,
  StripeExpressCheckoutElementClickEvent,
  ClickResolveDetails,
  StripeExpressCheckoutElementOptions,
} from '@stripe/stripe-js'

import { TGetItemFn } from '@/config/types'
import { CustomDOMEvents } from '@/enums/customDOMEvents'
import { PageName } from '@/enums/pageName'
import {
  CheckoutFlowVersion,
  PaymentMethod as PaymentMethodEnum,
  PaymentPlatform,
} from '@/enums/payments'
import { getPath } from '@/utils/urlUtils'

import { ApiService } from '@/api'
import { TPurchaseContentPayload } from '@/api/types'
import { emailKey } from '@/auth/types'
import { useAuth } from '@/auth/useAuth'
import { resetContactStateCheckTimeout } from '@/commonPages/paymentScreenPage/hooks/contactState/useCheckContactState'
import { contactNameKey } from '@/core/constants'
import { ISessionContext } from '@/core/session'
import { useSession } from '@/core/session/useSession'
import { trackLTPPaymentInfoAdded, trackLTPPurchaseStarted } from '@/events/events'

import { TLTPWalletPaymentButtonV2Props } from './types'

const getResultUrl = (
  paymentResultUrl: string,
  paymentMethod: PaymentMethodEnum,
  successPage: PageName
): URL => {
  const fail = PageName.ltpPaymentFailed
  const url = new URL(paymentResultUrl)
  url.searchParams.set('success_page', successPage)
  url.searchParams.set('fail_page', fail)
  url.searchParams.set('payment_method', paymentMethod)

  return url
}

function prepareContentPayload(
  getItem: TGetItemFn,
  email: string,
  sessionContext: ISessionContext,
  config: IConfig,
  contentPriceId: string
): TPurchaseContentPayload {
  const contactName = getItem(contactNameKey)
  const sessionId = sessionContext.sessionId
  const utmParams = sessionContext.utmParams
  const fbParams = sessionContext.fbParams || {}
  const gaParams = sessionContext.gaParams || {}
  const firstVisitAt = sessionContext.fva
  const firstVisitAtDiff: number = +new Date() - +new Date(firstVisitAt)

  const testEmail = getItem('testEmail')
  if (testEmail) {
    console.log(`testEmail '${testEmail} detected`)
  }

  return {
    session_id: sessionId,
    page_set_id: config.PAGE_SET_ID,
    email: email,
    test_email: testEmail,
    utm_params: {
      ...utmParams,
      ...fbParams,
      ...gaParams,
    },
    first_visit_at_diff: firstVisitAtDiff,
    name: contactName,
    internal_content_price_id: contentPriceId,
  }
}

const eventOnStripeWalletButtonClick = new Event(CustomDOMEvents.ON_STRIPE_WALLET_BUTTON_CLICK)

export const LTPWalletPaymentButtonV2 = ({
  contentDetails,
  getItem,
  setItem,
  showDelimiter,
  buttonsDelimiterText,
  pageName,
  config,
  stripeButtonTheme,
  getStripeWalletButtonStyles,
  onReady,
  successRedirectTo,
}: TLTPWalletPaymentButtonV2Props) => {
  const { user } = useAuth()
  const sessionContext = useSession()
  const navigate = useNavigate()
  const stripe = useStripe()
  const elements = useElements()
  const [walletAvailable, setWalletAvailable] = useState<
    PaymentMethodEnum.APPLE_PAY | PaymentMethodEnum.GOOGLE_PAY | false | undefined
  >(undefined)

  const stripeButtonStyles = useMemo<StripeExpressCheckoutElementOptions>(
    () => getStripeWalletButtonStyles(stripeButtonTheme, CheckoutFlowVersion.V2),
    [getStripeWalletButtonStyles, stripeButtonTheme]
  )

  const onReadyFn = (event: StripeExpressCheckoutElementReadyEvent): void => {
    if (!event.availablePaymentMethods) {
      // No buttons will show
      setWalletAvailable(false)
    } else {
      if (event.availablePaymentMethods.googlePay) {
        setWalletAvailable(PaymentMethodEnum.GOOGLE_PAY)
      } else if (event.availablePaymentMethods.applePay) {
        setWalletAvailable(PaymentMethodEnum.APPLE_PAY)
      }
    }
    if (typeof onReady === 'function') {
      onReady(event)
    }
  }

  const onClick = ({ resolve }: StripeExpressCheckoutElementClickEvent) => {
    document.dispatchEvent(eventOnStripeWalletButtonClick)
    const amount = contentDetails.price
    const options: ClickResolveDetails = {
      emailRequired: true,
      lineItems: [
        {
          name: contentDetails.name,
          amount: parseFloat((amount * 100).toFixed(2)),
        },
      ],
    }
    resolve(options)
  }

  const onLoadError = (): void => {}

  const onConfirm = async (event: StripeExpressCheckoutElementConfirmEvent): Promise<void> => {
    if (!stripe || !elements) {
      // Stripe.js hasn't loaded yet.
      // Make sure to disable form submission until Stripe.js has loaded.
      return
    }

    if (!event.billingDetails?.email) {
      console.log('no email')
      navigate(getPath(successRedirectTo))
      return
    }

    const { error: submitError } = await elements.submit()
    if (submitError) {
      console.log(submitError.message)
      // TODO: redirect?
      return
    }

    let paymentMethod: PaymentMethodEnum
    switch (event.expressPaymentType) {
      case 'apple_pay':
        paymentMethod = PaymentMethodEnum.APPLE_PAY
        break
      case 'google_pay':
        paymentMethod = PaymentMethodEnum.GOOGLE_PAY
        break
      default:
        throw Error('Unsupported payment method')
    }
    setItem('paymentMethod', paymentMethod)
    setItem('paymentPlatform', PaymentPlatform.STRIPE)

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const promisesList: Promise<any>[] = [
      trackLTPPurchaseStarted(
        pageName,
        config.PARTNER_ID,
        config.APP_ID,
        config.PAGE_SET_ID,
        contentDetails.internalPriceId,
        paymentMethod,
        PaymentPlatform.STRIPE,
        sessionContext,
        user
      ),
    ]

    promisesList.push(
      trackLTPPaymentInfoAdded(
        pageName,
        config.PARTNER_ID,
        config.APP_ID,
        config.PAGE_SET_ID,
        contentDetails.internalPriceId,
        paymentMethod,
        PaymentPlatform.STRIPE,
        sessionContext,
        user,
        [],
        [event.expressPaymentType]
      )
    )

    const redirectToResultPage = async (paymentResultUrl: string, clientSecret: string) => {
      const redirectTo = getResultUrl(paymentResultUrl, paymentMethod, successRedirectTo)

      const intent = await stripe.retrievePaymentIntent(clientSecret)
      if (intent.error) {
        navigate(getPath(PageName.ltpPaymentFailed))
        return
      }
      redirectTo.searchParams.set('payment_intent', intent.paymentIntent.id)

      Promise.all(promisesList).finally(() => {
        window.location.href = redirectTo.href
      })
    }

    resetContactStateCheckTimeout(setItem)
    //  wallet_name: event.expressPaymentType,
    const payload = prepareContentPayload(
      getItem,
      event.billingDetails?.email,
      sessionContext,
      config,
      contentDetails.internalPriceId
    )

    let response
    try {
      response = await ApiService.purchaseContent(payload)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.log('subscribe error', error)

      if (error && error.response) {
        if (error.response.status === 400) {
          if (error.response.data?.email === 'Invalid email') {
            setItem(emailKey, '')

            return
          }
        } else if (error.response.status === 409) {
          navigate(getPath(successRedirectTo))
          return
        }
        if (error.response.data?.email) {
          setItem(emailKey, error.response.data.email)
        }
      }

      navigate(getPath(PageName.ltpPaymentFailed))
      return
    }

    const {
      client_secret: clientSecret,
      payment_result_url: paymentResultUrl,
      email: responseEmail,
    } = response.data

    setItem(emailKey, responseEmail)

    try {
      const { error } = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: getResultUrl(paymentResultUrl, paymentMethod, successRedirectTo).href,
        },
      })

      if (error) {
        console.error('error2', error)
        redirectToResultPage(paymentResultUrl, clientSecret)
        return
      }
    } catch (error) {
      redirectToResultPage(paymentResultUrl, clientSecret)
    }
  }

  let containerClassName = 'stripe-wallet-container'
  if (walletAvailable === PaymentMethodEnum.GOOGLE_PAY) {
    containerClassName += ' gp-wallet-container'
  } else if (walletAvailable === false) {
    return null
  }

  return (
    <>
      {showDelimiter && (
        <div className="wallet-payment-button-delimiter">{buttonsDelimiterText}</div>
      )}
      <div className={containerClassName}>
        <ExpressCheckoutElement
          options={stripeButtonStyles}
          onClick={onClick}
          onConfirm={onConfirm}
          onLoadError={onLoadError}
          onReady={onReadyFn}
        />
      </div>
    </>
  )
}
